import { useTranslation } from "react-i18next";
import CollapsibleSection from "components/Section";
import { Grid } from "components/Grid";
import { LineChart } from "components/Charts/LineChart";
import { PieChart } from "components/Charts/PieChart";
import { TDataTableColumn } from "components/Grid/types";
import { getSubscribersReport } from "../api";
import { useReportData } from "../useReportData";
import { useReportFilters } from "../useReportFilters";
import { useGridDetailsContextMenu } from "components/Grid/hooks/useGridDetailsContextMenu";
import { KpiCard } from "../components/KpiCard";
import { ReportSubHeader } from "../components/ReportSubHeader";
import { ReportFiltersBar } from "../components/ReportFiltersBar";
import { getJalaliDate } from "utils/dateTimeTools";
import i18n from "../../../../react-i18next";

/** Every grid on this page is capped, so the page scrolls instead of the browser. */
const GRID_HEIGHT = 380;

const recentSubscribersColumns: TDataTableColumn[] = [
  {
    dataField: "subscriptionNumber",
    caption: "subscriptionNumber",
    dataType: "string",
  },
  { dataField: "fullName", caption: "fullName", dataType: "string" },
  {
    dataField: "subscriberTypeKey",
    caption: "subscriberType",
    dataType: "string",
    calculateCellValue: (row: any) => i18n.t(row.subscriberTypeKey),
  },
  {
    dataField: "jurisdictionName",
    caption: "jurisdiction",
    dataType: "string",
  },
  {
    dataField: "registeredAt",
    caption: "registrationDate",
    dataType: "string",
    calculateCellValue: (row: any) =>
      getJalaliDate({ date: row.registeredAt }),
    sortOrder: "desc",
  },
];

const SubscribersReportView = () => {
  const { t } = useTranslation();
  const { filters, applyFilters, isReady } = useReportFilters();
  const {
    data,
    isLoading,
    isRefetching,
    lastUpdated,
    lastAttempt,
    refreshIntervalMs,
    refresh,
  } = useReportData(
    getSubscribersReport,
    filters,
    isReady
  );
  const subscriberDetailsContextMenu = useGridDetailsContextMenu((row) =>
    row?.id ? `/dashboard/subscribers/details/${row.id}` : undefined
  );

  return (
    <>
      <ReportSubHeader
        title={t("subscribersReport")}
        lastUpdated={lastUpdated}
        lastAttempt={lastAttempt}
        refreshIntervalMs={refreshIntervalMs}
        isLoading={isLoading}
        isRefetching={isRefetching}
        onRefresh={refresh}
      />
      <ReportFiltersBar
        filters={filters}
        onApply={applyFilters}
        isLoading={isLoading}
      />
      <div className="flex flex-col gap-4 p-4 h-full overflow-auto [&>*]:shrink-0">
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title={t("totalSubscribers")}
            value={data?.totalSubscribers}
            iconName="MdPeopleOutline"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("activeSubscribers")}
            value={data?.activeSubscribers}
            iconName="MdCheckCircleOutline"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("inactiveSubscribers")}
            value={data?.inactiveSubscribers}
            iconName="MdPersonOff"
            isLoading={isLoading}
          />
          <KpiCard
            title={t("newSubscribersThisMonth")}
            value={data?.newSubscribersThisMonth}
            iconName="MdPersonAddAlt"
            isLoading={isLoading}
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <CollapsibleSection
            title={t("monthlyNewSubscribers")}
            className="xl:col-span-2"
            collapsible={false}
            isLoading={isLoading}
            skeleton="chart"
          >
            {data && (
              <LineChart
                series={[
                  {
                    type: "column",
                    name: t("newSubscribers"),
                    data: data.monthlyNewSubscribers.map((item) => item.value),
                  },
                ]}
                xAxis={{
                  categories: data.monthlyNewSubscribers.map(
                    (item) => item.name
                  ),
                }}
                yAxis={{ title: { text: t("count") } }}
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title={t("usageBySubscriberType")}
            collapsible={false}
            isLoading={isLoading}
            skeleton="pie"
          >
            {data && (
              <PieChart
                series={[
                  {
                    type: "pie",
                    name: t("consumption"),
                    data: data.usageByType.map((item) => ({
                      name: item.name,
                      y: item.value,
                    })),
                  },
                ]}
              />
            )}
          </CollapsibleSection>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <CollapsibleSection
            title={t("subscribersByJurisdiction")}
            collapsible={false}
            isLoading={isLoading}
            skeleton="chart"
          >
            {data && (
              <LineChart
                series={[
                  {
                    type: "bar",
                    name: t("subscribers"),
                    data: data.byJurisdiction.map((item) => item.value),
                  },
                ]}
                xAxis={{
                  categories: data.byJurisdiction.map((item) => item.name),
                }}
                yAxis={{ title: { text: t("count") } }}
              />
            )}
          </CollapsibleSection>
          <CollapsibleSection
            title={t("recentSubscribers")}
            className="xl:col-span-2"
            collapsible={false}
            isLoading={isLoading}
            skeleton="table"
            minHeight={GRID_HEIGHT}
          >
            <Grid
              gridKey="subscribersReportRecent"
              id="subscribersReportRecentGrid"
              gridName={t("recentSubscribers")}
              dataSource={data?.recentSubscribers || []}
              keyExpr="id"
              columns={recentSubscribersColumns}
              rowContextMenu={subscriberDetailsContextMenu}
              height={GRID_HEIGHT}
              hasSearchPanel={false}
              hasPagination={false}
              hasGroupingFeatures={false}
              hasFilterBuilder={false}
              allowedChangeScrolling={false}
            />
          </CollapsibleSection>
        </div>
      </div>
    </>
  );
};

export default SubscribersReportView;
